import { FC, useMemo, useEffect, useState } from 'react'
import { WithAssetSell, FutureLayerUID, WithAssetsAndDebts } from 'components/features/future/types'
import { FutureInputDecoration } from 'components/features/future/FutureInputDecoration/FutureInputDecoration'
import find from 'lodash/find'
import { useFormContext } from 'react-hook-form'
import Checkbox from 'components/atoms/inputs/Checkbox'
import styles from 'components/features/future/SellAssetFields/SellAssetFields.styl'
import { FutureFieldSlider } from 'components/features/future/FutureField/FutureFieldSlider'
import { maskCurrencyFromZero } from 'components/atoms/inputs/maskedInput'
import { usePrev } from 'hooks/usePrev'
import { useSellAssetHints } from 'components/features/future/helpers'
import { useMutableRef } from 'hooks/useMutableRef'
import isEqual from 'lodash/isEqual'
import { AssetsList } from 'components/features/future/AssetsList/AssetsList'
import { layerHintsSelector } from 'components/features/future/selectors'
import { useStoreSelector } from 'helpers/reactReduxHelpers'

type SellAssetFieldsProps = {
  uid?: FutureLayerUID
  type: 'car' | 'home'
  errors: Partial<Record<'debt_uids' | 'asset_uid' | 'sales_price', string>>
}
export const SellAssetFields: FC<SellAssetFieldsProps> = (props) => {
  const { errors, uid, type } = props
  const { watch, register, trigger, formState, setValue } = useFormContext<WithAssetSell & { sales_price: number }>()
  const [asset_uid] = watch(['asset_uid', 'sales_price'])
  const setValueRef = useMutableRef(setValue)


  // If bugs occur, please check how the old version of this function worked,
  // passing 'deps' and 'requiredFields' parameters

  // const [hints, setHints] = useState<WithAssetsAndDebts | null>(null)
  // useHints<'sell_home' | 'sell_car'>(setHints, ['when.slug', 'when.years', 'when.age', 'when.month'] as any, ['when.slug'] as any)
  useSellAssetHints()
  const layerHints = useStoreSelector(layerHintsSelector)
  const hints = layerHints.data as WithAssetsAndDebts | null

  const [updateSellPriceTrigger, updateSellPrice] = useState({})

  const prevHints = usePrev(hints)
  const prevHintsRef = useMutableRef(prevHints)
  useEffect(() => {
    const isHintsChanged = !isEqual(prevHintsRef.current, hints)
    if (!isHintsChanged) {
      return
    }
    updateSellPrice({})
  }, [hints, prevHintsRef])

  useEffect(() => {
    if (!asset_uid) {
      return
    }

    updateSellPrice({})
  }, [asset_uid])

  const assetUidRef = useMutableRef(asset_uid)
  const hintsRef = useMutableRef(hints)
  const dirtyFieldsRef = useMutableRef(formState.dirtyFields)
  useEffect(() => {
    if (!hintsRef.current || !assetUidRef.current || dirtyFieldsRef.current.sales_price || !!uid) {
      return
    }
    const asset = find(hintsRef.current.assets, { uid: assetUidRef.current })
    if (!asset) {
      return
    }
    const { value } = asset
    setValueRef.current('sales_price', value)
  }, [updateSellPriceTrigger, setValueRef, assetUidRef, hintsRef, dirtyFieldsRef, uid])

  const assetsChild = useMemo(() => (
    <FutureInputDecoration label={ `Which ${ type === 'car' ? 'car' : 'home' }?` } error={ errors.asset_uid }>
      <AssetsList
        name='asset_uid'
        type={ type }
        date={ hints ? hints.when_month : '' }
        assets={ hints ? hints.assets : null }
      />
    </FutureInputDecoration>
  ), [hints, errors.asset_uid, type])

  const isLoanLinked = useMemo(() => {
    const option = find(hints ? hints.assets : [], { uid: asset_uid })
    if (!option) {
      return true
    }
    return option.is_linked
  }, [hints, asset_uid])

  useEffect(() => {
    if (errors.asset_uid) {
      trigger('asset_uid')
    }
  }, [hints, errors, trigger])

  const debtsChild = useMemo(() => {
    if (isLoanLinked || !hints || !hints.debts || !hints.debts.length) {
      return null
    }

    return (
      <FutureInputDecoration label='Associated loan' error={ errors.debt_uids }>
        { hints.debts.map((item) => (
          <div key={ item.uid } className={ styles.checkbox }>
            <Checkbox
              register={ register }
              name='debt_uids'
              value={ item.uid }
              label={ item.name }
            />
          </div>
        )) }

      </FutureInputDecoration>
    )
  }, [errors.debt_uids, isLoanLinked, hints, register])

  return (
    <>
      { assetsChild }
      { debtsChild }

      <FutureFieldSlider
        label='Sale Price'
        name='sales_price'
        mask={ maskCurrencyFromZero }

        error={ errors.sales_price }

        min={ 0 }
        max={ 2000000 }
        step={ 10000 }
      />
    </>
  )
}
